import * as styles from "./DogListingsFilters.module.scss";

import React, { useCallback, useEffect, useState } from "react";
import type { ButtonProps } from "../../../components/common";

import CurvedBorderShallow from "../../../components/borders/CurvedBorderShallow";
import { Filter, Sort } from "../../../components/common/Icons";
import SectionDogList from "../../../containers/sections/SectionDogList";
import { useDogSearchApi, usePageManager } from "../../../context";
import { Colours } from "../../../utils/colours";

import cx from "classnames";

export interface FiltersButtonProps extends ButtonProps {
	buttonId?: string;
	value?: string;
	width?: string;
	count?: number;
	options?: { title: string; onClick: () => void }[];
	onHide?: () => void;
}

export const FiltersButton: React.FC<FiltersButtonProps> = ({
	buttonId,
	title,
	className,
	value,
	IconLeft,
	onClick,
	options,
	count,
	onHide,
}) => {
	return (
		<button
			type="button"
			id={buttonId}
			className={cx(styles.filtersbutton, className)}
			onClick={onClick}
			tabIndex={0}
			onKeyDown={(e) => {
				if (e.key === "Enter" || e.key === "ArrowDown") {
					e.preventDefault();
					onClick();
				}
			}}
			aria-expanded={options ? "true" : "false"}
		>
			<div className={styles.iconwrapper}>
				<IconLeft />
			</div>
			<div className={styles.textwrapper}>
				{value ? (
					<>
						<div className={styles.titlewithvalue}>{title}</div>
						<div className={styles.value}>{value}</div>
					</>
				) : (
					<div className={styles.title}>{title}</div>
				)}
			</div>
			<span className={styles.countwrapper}>
				{count > 0 && <div className={styles.filterselectedcount}>{count}</div>}
			</span>

			{options && (
				<div className={styles.sortoptions}>
					{options?.map((option, i) => (
						<button
							type="button"
							id={`filter-option-${i}`}
							key={option.title}
							className={styles.sortoption}
							onClick={option.onClick}
							tabIndex={0}
							onKeyDown={(e) => {
								if (e.key === "Enter") option.onClick();
								if (e.key === "Escape") onHide?.();
								if (e.key === "Tab" && i === options.length - 1) {
									e.preventDefault();
									document.getElementById("filter-option-0")?.focus();
								}
							}}
						>
							{option.title}
						</button>
					))}
				</div>
			)}
		</button>
	);
};

export const DogListingsFilters: React.FC = () => {
	const [showSortOptions, setShowSortOptions] = useState(false);
	const {
		results,
		resultsCount,
		sortBy,
		userLocation,
		filtersCount,
		dogApiLoading,
		queryStrings,
		useDogSearchQuery,
		onChangeFilterView,
		onUpdateSort,
		onFirstPage,
		onPreviousPage,
		onNextPage,
		onLastPage,
		pageCount,
		currentPage,
	} = useDogSearchApi();

	const { pagination } = usePageManager();

	// biome-ignore lint/correctness/useExhaustiveDependencies: We only want this to run once
	useEffect(() => {
		useDogSearchQuery();
	}, []);

	const getSortName = () => {
		switch (sortBy) {
			case "NEW":
				return "Newest Listed";
			case "OLD":
				return "Oldest Listed";
			case "A":
				return "Alphabetical (A - Z)";
			case "Z":
				return "Alphabetical (Z - A)";
			case "NEAR":
				return "Distance (Closest to Furthest)";
			case "FAR":
				return "Distance (Furthest to Closest)";
		}
	};

	const getSortOptions = useCallback(() => {
		if (!showSortOptions) return;
		const defaultSortOptions = [
			{
				title: "Newest Listed",
				onClick: () => onUpdateSort("NEW"),
			},
			{
				title: "Oldest Listed",
				onClick: () => onUpdateSort("OLD"),
			},
			{
				title: "Alphabetical (A - Z)",
				onClick: () => onUpdateSort("A"),
			},
			{
				title: "Alphabetical (Z - A)",
				onClick: () => onUpdateSort("Z"),
			},
		];
		if (userLocation?.latitude)
			defaultSortOptions.push(
				...[
					{
						title: "Distance (Closest to Furthest)",
						onClick: () => onUpdateSort("NEAR"),
					},
					{
						title: "Distance (Furthest to Closest)",
						onClick: () => onUpdateSort("FAR"),
					},
				],
			);

		return defaultSortOptions;
	}, [showSortOptions, userLocation?.latitude, onUpdateSort]);
	return (
		<div className={styles.doglistingouter}>
			<div className={styles.doglistinginner}>
				<div className={styles.filters}>
					<FiltersButton
						buttonId={"dogs-filter-button"}
						IconLeft={Filter}
						className={styles.allfilters}
						title={"Filters"}
						onClick={() => onChangeFilterView("Any")}
						count={filtersCount}
					/>
					<FiltersButton
						IconLeft={Sort}
						className={styles.sortfilters}
						title={"Sort"}
						value={getSortName()}
						onClick={() => setShowSortOptions((show) => !show)}
						options={getSortOptions()}
					/>
				</div>
			</div>
			<div className={styles.listings}>
				<div className={styles.bordersection}>
					<CurvedBorderShallow
						className={styles.border}
						fill={Colours.SANDY_FUR}
						rotation={0}
					/>
				</div>
				<div className={styles.results}>
					<SectionDogList
						dogData={results || []}
						totalResults={resultsCount}
						onFirstPage={onFirstPage}
						onLastPage={onLastPage}
						onPreviousPage={onPreviousPage}
						onNextPage={onNextPage}
						userLocation={userLocation}
						dogApiLoading={dogApiLoading}
						queryStrings={queryStrings}
						pagination={pagination}
						pageCount={pageCount}
						currentPage={currentPage}
					/>
				</div>
			</div>
		</div>
	);
};

export default DogListingsFilters;
